import React, { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, Lock, Loader2, Check, ArrowLeft, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function LeadRegistrationDialog({ open, onOpenChange }) {
  const { login } = useAuth();
  const [step, setStep] = useState('form'); // form | code
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setStep('form');
    setName('');
    setEmail('');
    setCode('');
    setError('');
    setLoading(false);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleSend = async (e) => {
    e?.preventDefault();
    setError('');
    if (name.trim().length < 2) return setError('Ingresa tu nombre completo.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return setError('Ingresa un correo válido.');
    setLoading(true);
    try {
      await axios.post(`${API}/otp/send`, { name: name.trim(), email: email.trim().toLowerCase() });
      toast.success('Código enviado. Revisa tu correo.');
      setStep('code');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo enviar el código.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) return setError('El código debe tener 6 dígitos.');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/otp/verify`, {
        email: email.trim().toLowerCase(),
        code: code.trim(),
      });
      login(data);
      toast.success('¡Verificado! Resultados desbloqueados.');
      handleClose(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Código incorrecto.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="lead-registration-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-xl bg-blue-100">
              <ShieldCheck className="h-5 w-5 text-[#1A56DB]" />
            </div>
            <div>
              <DialogTitle className="text-xl">Desbloquea tus resultados</DialogTitle>
              <DialogDescription className="text-xs mt-1">
                Verifica tu correo corporativo para acceder a la estimación completa.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {step === 'form' ? (
          <motion.form
            key="form"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSend}
            className="space-y-4 mt-2"
          >
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1.5">Nombre completo</label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Laura Martínez"
                className="h-11"
                data-testid="lead-name-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1.5">Correo corporativo</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nombre@tuempresa.com"
                  className="h-11 pl-9"
                  data-testid="lead-email-input"
                />
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                No aceptamos correos personales (gmail, hotmail, outlook, yahoo, etc.).
              </p>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="lead-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-[#1A56DB] to-[#3B82F6] text-white font-semibold"
              data-testid="lead-send-otp-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enviar código de verificación'}
            </Button>

            <p className="text-[11px] text-slate-500 text-center leading-relaxed">
              Al continuar aceptas nuestro{' '}
              <a
                href="https://vitaminaonline.com.mx/aviso-de-privacidad"
                target="_blank"
                rel="noreferrer noopener"
                className="text-[#1A56DB] hover:underline"
              >
                Aviso de Privacidad
              </a>
              .
            </p>
          </motion.form>
        ) : (
          <motion.form
            key="code"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleVerify}
            className="space-y-4 mt-2"
          >
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
              Enviamos un código de 6 dígitos a <strong>{email}</strong>. Expira en 10 minutos.
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1.5">Código de verificación</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="h-12 pl-9 text-center text-xl font-mono tracking-[0.5em]"
                  data-testid="lead-otp-input"
                  autoFocus
                />
              </div>
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="lead-error">
                {error}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => { setStep('form'); setError(''); setCode(''); }}
                className="gap-2"
                data-testid="lead-back-button"
              >
                <ArrowLeft className="h-4 w-4" /> Volver
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-11 bg-gradient-to-r from-[#1A56DB] to-[#3B82F6] text-white font-semibold"
                data-testid="lead-verify-otp-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Check className="h-4 w-4 mr-1" /> Verificar código</>)}
              </Button>
            </div>
            <button
              type="button"
              onClick={handleSend}
              className="text-xs text-[#1A56DB] hover:underline w-full text-center"
              disabled={loading}
              data-testid="lead-resend-otp-button"
            >
              Reenviar código
            </button>
          </motion.form>
        )}
      </DialogContent>
    </Dialog>
  );
}
